'use client'

import Link from 'next/link'
import {
  Calendar,
  Clock,
  ArrowLeft,
  ArrowRight,
} from 'lucide-react'
import { BOOK_A_DEMO_URL } from '@/lib/constants'
import { AttributedCtaLink } from '@/components/conversion/AttributedCtaLink'
import type { BlogPost } from '@/lib/supabase'
import ArticleClient from '@/components/blog/ArticleClient'
import TableOfContents, { injectHeadingIds } from '@/components/blog/TableOfContents'
import ShareWidget from '@/components/blog/ShareWidget'
import { useInView } from '@/hooks/useInView'

/* ── Related Article Card ────────────────────────────────────── */
function RelatedCard({ post, index }: { post: BlogPost; index: number }) {
  const { ref, isInView } = useInView()

  const publishedDate = new Date(post.published_at).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })

  // Map index to delay class (1-indexed)
  const delayClass = `anim-delay-${Math.min(index + 1, 8)}`

  return (
    <div
      ref={ref}
      className={`anim-fade-in-up ${delayClass} ${isInView ? 'in-view' : ''}`}
    >
      <Link href={`/blog/${post.slug}`} className="group block h-full">
        <article className="glass-card-premium blog-card-accent p-6 h-full flex flex-col transition-all duration-300 hover:-translate-y-1">
          {post.categories && post.categories.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {post.categories.map((cat) => (
                <span
                  key={cat.id}
                  className="px-2.5 py-0.5 text-[10px] font-medium bg-red-600/10 text-red-400 rounded-full border border-red-600/20 uppercase tracking-wider"
                >
                  {cat.name}
                </span>
              ))}
            </div>
          )}
          <h3 className="text-lg font-semibold text-white mb-3 group-hover:text-red-400 transition-colors duration-300 line-clamp-2 leading-snug">
            {post.title}
          </h3>
          <p className="text-sm text-zinc-400 mb-4 line-clamp-3 flex-1 leading-relaxed">
            {post.excerpt || post.meta_description}
          </p>
          <div className="flex items-center justify-between text-xs text-zinc-500 pt-4 border-t border-white/[0.04]">
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1">
                <Calendar size={12} />
                {publishedDate}
              </span>
              <span className="flex items-center gap-1">
                <Clock size={12} />
                {post.reading_time_minutes} min
              </span>
            </div>
            <ArrowRight
              size={14}
              className="text-zinc-600 group-hover:text-red-400 group-hover:translate-x-0.5 transition-all duration-300"
            />
          </div>
        </article>
      </Link>
    </div>
  )
}

/* ── Main Article Content Component ──────────────────────────── */
export function ArticlePageContent({
  post,
  relatedPosts,
}: {
  post: BlogPost
  relatedPosts: BlogPost[]
}) {
  const { ref: heroRef, isInView: heroInView } = useInView()
  const { ref: contentRef, isInView: contentInView } = useInView()
  const { ref: continueRef, isInView: continueInView } = useInView()

  const publishedDate = new Date(post.published_at).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })

  const updatedDate =
    post.updated_at && new Date(post.updated_at) > new Date(post.published_at)
      ? new Date(post.updated_at).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })
      : null

  // Inject heading IDs for TOC anchoring
  const processedContent = injectHeadingIds(post.content)

  // Author initials
  const initials = post.author
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)

  return (
    <ArticleClient readingTime={post.reading_time_minutes}>
      <div className="min-h-screen">
        {/* ── Article Header ──────────────────────────────── */}
        <section className="relative overflow-hidden pt-28 sm:pt-32 pb-12 sm:pb-16" ref={heroRef}>
          {/* Gradient mesh backdrop */}
          <div className="absolute inset-0 gradient-mesh-hero pointer-events-none opacity-40" />
          {/* Top fade */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-black pointer-events-none" />

          <div className="relative z-10 max-w-3xl mx-auto px-6">
            {/* Breadcrumb */}
            <nav
              className={`flex items-center gap-2 text-xs text-zinc-500 mb-6 anim-fade-in-left anim-delay-1 ${heroInView ? 'in-view' : ''}`}
              aria-label="Breadcrumb"
            >
              <Link href="/" className="hover:text-white transition-colors">Home</Link>
              <span className="text-zinc-700">/</span>
              <Link href="/blog" className="hover:text-white transition-colors">Blog</Link>
              <span className="text-zinc-700">/</span>
              <span className="text-zinc-400 truncate max-w-[200px]">{post.title}</span>
            </nav>

            {/* Categories */}
            {post.categories && post.categories.length > 0 && (
              <div className={`flex flex-wrap gap-2 mb-4 anim-fade-in-up anim-delay-1 ${heroInView ? 'in-view' : ''}`}>
                {post.categories.map((cat, i) => (
                  <span
                    key={cat.id}
                    className={`px-3 py-1 text-[11px] font-semibold bg-red-600/10 text-red-400 rounded-full border border-red-600/20 uppercase tracking-wider`}
                  >
                    {cat.name}
                  </span>
                ))}
              </div>
            )}

            {/* Title */}
            <h1
              className={`text-3xl sm:text-4xl lg:text-[2.75rem] font-bold leading-[1.15] mb-6 tracking-[-0.02em] text-white anim-fade-in-up anim-delay-2 ${heroInView ? 'in-view' : ''}`}
            >
              {post.title}
            </h1>

            {/* Meta bar */}
            <div
              className={`flex items-center flex-wrap gap-x-5 gap-y-3 text-sm text-zinc-500 anim-fade-in-up anim-delay-3 ${heroInView ? 'in-view' : ''}`}
            >
              <span className="flex items-center gap-2">
                <span className="w-7 h-7 rounded-full bg-red-600/15 flex items-center justify-center text-[10px] font-bold text-red-400 shrink-0">
                  {initials}
                </span>
                <span className="text-zinc-300 font-medium text-sm">{post.author}</span>
              </span>
              <span className="flex items-center gap-1.5 text-sm">
                <Calendar size={13} />
                {publishedDate}
              </span>
              {updatedDate && (
                <span
                  className="flex items-center gap-1.5 text-xs text-zinc-500"
                  title={`This article was last updated on ${updatedDate}`}
                >
                  Updated {updatedDate}
                </span>
              )}
              <span className="flex items-center gap-1.5 text-sm">
                <Clock size={13} />
                {post.reading_time_minutes} min read
              </span>
              <div className="sm:ml-auto">
                <ShareWidget title={post.title} />
              </div>
            </div>
          </div>
        </section>

        {/* ── Divider ─────────────────────────────────────── */}
        <div className="max-w-3xl mx-auto px-6">
          <div className="h-px bg-gradient-to-r from-transparent via-zinc-800 to-transparent" />
        </div>

        {/* ── Article Content ─────────────────────────────── */}
        <section className="py-12 sm:py-16" ref={contentRef}>
          <div
            className={`max-w-7xl mx-auto px-6 grid lg:grid-cols-[1fr_280px] gap-12 xl:gap-16 anim-fade-in-up ${contentInView ? 'in-view' : ''}`}
          >
            {/* Main Content — contained prose */}
            <div className="min-w-0">
              <article
                className="article-content"
                dangerouslySetInnerHTML={{ __html: processedContent }}
              />
            </div>

            {/* Sidebar */}
            <aside className="hidden lg:block">
              <div className="sticky top-24 space-y-5">
                <TableOfContents htmlContent={post.content} />

                {/* CTA Card */}
                <div
                  className={`glass-card-premium p-5 relative overflow-hidden group anim-fade-in-right anim-delay-3 ${contentInView ? 'in-view' : ''}`}
                >
                  <div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                    style={{
                      background: 'radial-gradient(ellipse at 50% 0%, rgba(220,38,38,0.08) 0%, transparent 60%)',
                    }}
                  />
                  <div className="relative z-10">
                    <h3 className="text-base font-semibold text-white mb-1.5">
                      Ready to try BuilderLync?
                    </h3>
                    <p className="text-xs text-zinc-400 mb-3 leading-relaxed">
                      See how one platform can replace your entire tool stack.
                    </p>
                    <AttributedCtaLink
                      href={BOOK_A_DEMO_URL}
                      page="blog"
                      cta="Sidebar — Book a Demo"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-primary w-full justify-center text-sm group/btn"
                    >
                      Book a Demo
                      <ArrowRight size={14} className="transition-transform duration-200 group-hover/btn:translate-x-0.5" />
                    </AttributedCtaLink>
                  </div>
                </div>

                {/* Platform pages — spoke-to-pillar internal linking */}
                <div className="glass-card-premium p-5">
                  <h3 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-3">
                    Explore the Platform
                  </h3>
                  <div className="space-y-2">
                    {[
                      { href: '/roofing-crm', label: 'Roofing CRM' },
                      { href: '/roof-estimating-software', label: 'Roof Estimating Software' },
                      { href: '/roof-measurement-reports', label: 'Roof Measurement Reports' },
                      { href: '/instant-roof-quotes', label: 'Instant Roof Quotes' },
                    ].map((link) => (
                      <Link
                        key={link.href}
                        href={link.href}
                        className="block py-2 text-sm text-zinc-300 hover:text-red-400 transition-colors border-b border-white/[0.04] last:border-0 last:pb-0 flex items-center justify-between group"
                      >
                        <span>{link.label}</span>
                        <ArrowRight size={12} className="text-zinc-600 group-hover:text-red-400 group-hover:translate-x-0.5 transition-all" />
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Related Posts */}
                {relatedPosts.length > 0 && (
                  <div
                    className={`glass-card-premium p-5 anim-fade-in-right anim-delay-5 ${contentInView ? 'in-view' : ''}`}
                  >
                    <h3 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-3">
                      Related Articles
                    </h3>
                    <div className="space-y-3">
                      {relatedPosts.map((related) => (
                        <Link
                          key={related.id}
                          href={`/blog/${related.slug}`}
                          className="block group py-2 border-b border-white/[0.04] last:border-0 last:pb-0"
                        >
                          <h4 className="text-sm font-medium text-zinc-300 group-hover:text-red-400 transition-colors line-clamp-2 leading-snug mb-0.5">
                            {related.title}
                          </h4>
                          <p className="text-[11px] text-zinc-500 flex items-center gap-1">
                            <Clock size={10} />
                            {related.reading_time_minutes} min read
                          </p>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </aside>
          </div>
        </section>

        {/* ── Continue Reading ─────────────────────────────── */}
        {relatedPosts.length > 0 && (
          <section className="py-16 sm:py-20 border-t border-white/[0.04]">
            <div className="max-w-6xl mx-auto px-6">
              <div
                ref={continueRef}
                className={`text-center mb-10 anim-fade-in-up ${continueInView ? 'in-view' : ''}`}
              >
                <h2 className="text-2xl sm:text-3xl font-bold mb-3">
                  Continue <span className="gradient-text-red">Reading</span>
                </h2>
                <p className="text-zinc-400 max-w-lg mx-auto text-sm">
                  More expert guides and insights for roofing contractors.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedPosts.map((rp, i) => (
                  <RelatedCard key={rp.id} post={rp} index={i} />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ── Back to Blog ────────────────────────────────── */}
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="border-t border-white/[0.06] pt-8">
            <Link
              href="/blog"
              className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors group text-sm"
            >
              <ArrowLeft size={14} className="group-hover:-translate-x-0.5 transition-transform" />
              Back to Blog
            </Link>
          </div>
        </div>
      </div>
    </ArticleClient>
  )
}
